import { useState } from 'react';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { useAccess } from '@/features/billing/use-access';
import { authClient } from '@/lib/auth-client';

/**
 * Shown on student pages until the email address is confirmed. The server blocks starting a test
 * either way; this tells the student why and lets them ask for a new link.
 */
export function VerifyEmailBanner() {
  const { data: session } = authClient.useSession();
  const access = useAccess(Boolean(session));
  const [state, setState] = useState<'idle' | 'sending' | 'sent' | 'failed'>('idle');

  const email = session?.user.email;
  if (!email || !access.data || access.data.emailVerified) return null;

  async function resend() {
    if (!email) return;
    setState('sending');
    const { error } = await authClient.sendVerificationEmail({
      email,
      callbackURL: `${window.location.origin}/dashboard?verified=1`,
    });
    setState(error ? 'failed' : 'sent');
  }

  return (
    <Alert tone="warning" title="Confirm your email to start a test" className="mb-6">
      <div className="grid gap-3">
        <p>
          We sent a link to <b>{email}</b>. Open it, then come back here.
        </p>
        <div className="flex flex-wrap items-center gap-3">
          <Button
            variant="secondary"
            size="sm"
            loading={state === 'sending'}
            disabled={state === 'sent'}
            onClick={() => void resend()}
          >
            {state === 'sent' ? 'Link sent' : 'Send the link again'}
          </Button>
          {state === 'failed' ? (
            <span className="text-small text-danger">We could not send it. Try again soon.</span>
          ) : null}
        </div>
      </div>
    </Alert>
  );
}
